import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Paper, LinearProgress } from '@material-ui/core';
import { Storage } from 'aws-amplify';
import API from '../../api-graphql';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../UI/AlertDialog';
import UploadForm from '../UploadForm';
import { CONSTANTS } from '../../constants/constants';
import PageContentError, { Types } from '../UI/PageContentError';

const useStyles = makeStyles(theme => ({
  paper: {
    maxWidth: '50%',
    margin: 'auto',
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(6))]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3)
    },
    [theme.breakpoints.down(600 + theme.spacing(6))]: {
      maxWidth: '100%'
    }
  }
}));

const DocumentUpdateView = (props) => {
  const { location: { params }, match, history } = props;

  const classes = useStyles();

  const [input, setInput] = useState(() => {
    if (params) return params.input;
    else return null;
  });
  const [files, setFiles] = useState();
  const [loading, setLoading] = useState(!params);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const FILE_TYPES_ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];

  useEffect(() => {
    if (input && input.principal) return;

    API.getDocument(match.params.number)
      .then(result => {
        const input = result.data.getDocument;
        setInput(input);
        setLoading(false);
      })
      .catch(error => {
        console.error(error);
        setError(t('templateUploadView:genericError'));
      });
  }, []);

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      const ext = /[.]\w{3,4}$/.exec(files[0].name)[0];

      if (!ext || !FILE_TYPES_ACCEPTED.includes(ext)) {
        const error = new Error('Wrong Extension');
        error.customMessage = `${t('docUpdate:extensionError')} ${FILE_TYPES_ACCEPTED.join(', ')}`;
        throw error;
      }

      if (input.status !== CONSTANTS.DOCUMENT.STATUS.INIT) {
        const error = new Error('Wrong document status');
        error.customMessage = t('docUpdate:statusError');
        throw error;
      }

      input.name = e.target.name.value;
      input.description = e.target.description.value;
      input.updatedAt = new Date().toISOString();
      input.fileUrl = input.fileUrl.replace(/[.]\w{3,4}$/, ext);
      input.principal.name = files[0].name;

      if (input.template) {
        input.documentTemplateId = input.template.id;
      }

      await Storage.put(input.fileUrl, files[0]);

      await API.updateDocument(input);
      history.goBack();
    } catch (e) {
      console.error('Error creating PDF', e);
      setLoading(false);
      setError(e.customMessage || t('templateUploadView:genericError'));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 0, position: 'absolute', left: 0, width: '100%', zIndex: 9999 }}>
        <LinearProgress />
      </div>
    );
  }

  return !input
    ? <PageContentError type={Types.NOT_FOUND} text={t(`error:notFound:documentNotFound`)} />
    : <Paper className={classes.paper}>
      <AlertDialog open={!!error} title='Error' description={error} setOpen={() => setError(null)} />
      <UploadForm
        handleSubmit={handleSubmit}
        handleCancel={history.goBack}
        setContent={setFiles}
        accept={FILE_TYPES_ACCEPTED.join(',')}
        title={t('docUpdate:title')}
        nameValue={input.name}
        descriptionValue={input.description}
        actionButtonLabel={t('docUpdate:actionButton')}
      />
    </Paper>
  ;
};

DocumentUpdateView.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default DocumentUpdateView;
